import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import { getRestaurantSettings } from '../actions/restaurantSettings';
import type { OpeningDay } from '../helpers/hours';

export interface RestaurantSettings {
  phone_primary: string;
  phone_secondary: string;
  email: string;
  address_line1: string;
  postal_code: string;
  city: string;
  country: string;
  enterprise_number: string;
  latitude: number;
  longitude: number;
  maps_url: string;
  parking_info: string;
  access_info: string;
  phone_hours: string;
  facebook_url: string;
  instagram_url: string;
  footer_text: string;
  default_share_image: string;
  carto_api_key: string;
  matomo_url: string;
  matomo_site_id: string;
  opening_hours: OpeningDay[];
}

// Used when the backend does not answer. Keep in sync with the
// defaults of IRestaurantSettings in the backend package.
const DEFAULTS: RestaurantSettings = {
  phone_primary: '010 60 15 71',
  phone_secondary: '0467 07 87 44',
  email: '',
  address_line1: 'Chaussée de Wavre 88',
  postal_code: '1370',
  city: 'Jodoigne',
  country: 'Belgique',
  enterprise_number: '',
  latitude: 50.7244,
  longitude: 4.858,
  maps_url:
    'https://www.google.com/maps/search/?api=1&query=Chauss%C3%A9e+de+Wavre+88%2C+1370+Jodoigne',
  parking_info: 'Gratuit, devant le restaurant',
  access_info: 'N91 · Sortie Jodoigne',
  phone_hours: 'Mardi au samedi, 10h–19h',
  facebook_url:
    'https://www.facebook.com/Restaurant-%C3%80-Ciel-Ouvert-103247041239868',
  instagram_url: 'https://www.instagram.com/acielouvert.jodoigne/',
  footer_text:
    'Restaurant de cuisine traditionnelle française. Produits du marché, ' +
    'salle baignée de jour, accueil de quartier.',
  default_share_image: '',
  carto_api_key: '',
  matomo_url: '',
  matomo_site_id: '',
  opening_hours: [],
};

const KEYS = Object.keys(DEFAULTS) as (keyof RestaurantSettings)[];

// A cleared field comes back as null. Show it empty, except for the
// coordinates, which the map needs as numbers, and the lists.
function parseSettings(data: Record<string, unknown>): RestaurantSettings {
  const parsed: Record<string, unknown> = { ...DEFAULTS };
  KEYS.forEach((key) => {
    const value = data?.[key];
    if (value === null || value === undefined) {
      if (Array.isArray(DEFAULTS[key])) parsed[key] = [];
      else if (typeof DEFAULTS[key] === 'number') parsed[key] = DEFAULTS[key];
      else parsed[key] = '';
    } else {
      parsed[key] = value;
    }
  });
  return parsed as unknown as RestaurantSettings;
}

export function useRestaurantSettings(): RestaurantSettings {
  const dispatch = useDispatch();
  const store = useStore();
  const data = useSelector(
    (state: any) => state.restaurantSettings?.data,
    shallowEqual,
  );

  // The server loads the settings for every page. Fetch them here only when
  // that failed. Read the store directly: several components mount at once.
  useEffect(() => {
    const current = (store.getState() as any).restaurantSettings;
    if (current?.loaded || current?.loading) return;
    const request: any = dispatch(getRestaurantSettings() as any);
    request?.catch?.(() => null);
  }, [dispatch, store]);

  return useMemo(() => (data ? parseSettings(data) : DEFAULTS), [data]);
}
